import React, { useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MenuItem, CircularProgress } from '@mui/material';
import { mkConfig, generateCsv, download } from "export-to-csv";

import { imputedFromAntibiotic } from '../AMRPredictions/imputationUtils.js';
import resistanceGenesReference from '../AMRPredictions/resistanceGenesReference';
import { sortByIdConfidence } from './utilities.js';
import { useActiveAnalysis, useIdentifiedSpecies, useAmrPredictions, useResistanceGenes, useImputations } from '../services/api/KeynomeAPIHooks.js';

const EXPORTABLE_MODEL_PERFORMANCE_LABELS = ['VH', 'H', 'M'];

export default function ExportKgASTPredictionsMenuItem(props) {   
    const {
        isDatasetLevel,
        run,
        samples,
        handleMenuClose,
        oneMenuItemClicked,
        setOneMenuItemClicked,
    } = props;
    const { setUserErrorMessage, setProgressMessage, hasAccessReportViruses } = useOutletContext();

    const { data: activeAnalyses, isLoading: analysesLoading } = useActiveAnalysis(samples?.map(sample => sample?.id));
    const { data: identifiedSpecies, isLoading: identifiedSpeciesLoading } = useIdentifiedSpecies(
        activeAnalyses?.filter(analysis => analysis?.attributes.status === "succeeded")?.map(analysis => analysis?.id)
    );
    const { data: amrPredictions, isLoading: amrPredictionsLoading } = useAmrPredictions(identifiedSpecies?.map(species => species?.id));
    const { data: resistanceGenes, isLoading: resistanceGenesLoading} = useResistanceGenes([...new Set(amrPredictions?.map(prediction => prediction?.attributes.identified_species_id))]);
    const { data: imputations, isLoading: imputationsLoading } = useImputations(
        amrPredictions
            ?.filter(prediction => ['VH', 'H'].includes(prediction?.attributes.model_performance_label))
            ?.map(prediction => prediction?.id)
    );

    const dataLoading = analysesLoading || identifiedSpeciesLoading || amrPredictionsLoading || resistanceGenesLoading || imputationsLoading;

    // TODO: dedupe this logic with extracting data for AMRPredictionsRows 
    const kgASTRowsBySpecies = useCallback((sample, analysis, species) => {
        const _amrPredictions = amrPredictions.filter(prediction => prediction.attributes.identified_species_id === species.id);
        const _resistanceGenes = resistanceGenes.filter(gene => gene.attributes.identified_species_id === species.id);

        const _amrPredictionsById = _amrPredictions.reduce((acc, pred) => {return {...acc, [pred.id]: pred}}, {})
        const _imputations = imputations.filter(imputation => Object.keys(_amrPredictionsById).includes(String(imputation.attributes.amr_model_prediction_id)));

        const commonColumns = {
            "dataset_name": run.attributes.name,
            "sample_name": sample.attributes.sample_name,
            "sample_type": sample.attributes.sample_type,
            "analysis_pipeline_version": analysis?.attributes.metadata.minion_pipeline_version,
            "analysis_started_at": analysis.attributes.created_at,
            "analysis_completed_at": analysis.attributes.updated_at,
            "organism": species.attributes.species,
            "antibiotic": "",
            "amr_call": "",
            "cgast_model_performance_class": "",
            "resistance_genes": "",
            "amr_call_imputed_from": "",
            "passes_qc": (
                !!species.attributes.reasons_for_qc_failure ?
                species.attributes.reasons_for_qc_failure.length === 0
                : ""
            ),
        };

        const kgASTDataByDrug = new Map();
        _amrPredictions
            .filter(prediction => EXPORTABLE_MODEL_PERFORMANCE_LABELS.includes(prediction?.attributes.model_performance_label))
            .forEach(prediction => {
                kgASTDataByDrug.set(
                    prediction.attributes.model_drug,
                    {
                        "antibiotic": prediction.attributes.model_drug,
                        "amr_call": prediction.attributes.amr_call,
                        "cgast_model_performance_class": prediction.attributes.model_performance_label,
                    }
                )
            });
        _imputations.forEach(imputation => {
            kgASTDataByDrug.set(
                imputation.attributes.drug,
                {
                    "antibiotic": imputation.attributes.drug,
                    "amr_call": imputation.attributes.call,
                    "amr_call_imputed_from": imputedFromAntibiotic(_amrPredictionsById, imputation.attributes),
                }
            )
        });
        _resistanceGenes.forEach(gene => {
            const nodeDetails = resistanceGenesReference[gene.attributes.hierarchy_node];
            if (!!nodeDetails) {
                nodeDetails.antibiotics.forEach(antibiotic => {
                    kgASTDataByDrug.set(
                        antibiotic,
                        {
                            ...(kgASTDataByDrug.get(antibiotic) || {}),
                            "antibiotic": antibiotic,
                            "resistance_genes": nodeDetails.display_name,
                        }
                    )
                });
            }
        });

        return Array.from(kgASTDataByDrug.values())
            ?.map(item => {return {...commonColumns, ...item};})
            ?.sort((a, b) => a.antibiotic.localeCompare(b.antibiotic));
    }, [run, amrPredictions, resistanceGenes, imputations]);

    const kgASTRows = useMemo(() => {
        if (dataLoading) return [];

        return (samples ?? []).map(sample => {
            const _activeAnalysis = activeAnalyses?.find(analysis => analysis?.attributes?.sample_id === sample?.id);
            const _identifiedSpecies = sortByIdConfidence(
                identifiedSpecies
                    .filter(species => species.attributes.analyses_id === _activeAnalysis?.id)
                    .filter(species => (hasAccessReportViruses || !species.attributes.is_virus))
            );

            if (!!!_activeAnalysis || _identifiedSpecies.length === 0) return [];

            return _identifiedSpecies?.map(species => kgASTRowsBySpecies(sample, _activeAnalysis, species)).flat();
        }).flat();
    }, [dataLoading, samples, activeAnalyses, identifiedSpecies, kgASTRowsBySpecies, hasAccessReportViruses]);

    const hasAnalyzedData = kgASTRows.length > 0;

    const handleExportClick = useCallback(async () => {
        setOneMenuItemClicked(true);
        setProgressMessage({"status": "inProgress", "message": "Exporting AMR Predictions report"});

        try {
            const csvConfig = mkConfig({
                useKeysAsHeaders: true,
                filename: (isDatasetLevel ? run.attributes.name : samples[0].attributes.sample_name ) + '_amr_predictions'
            });
            const csv = generateCsv(csvConfig)(kgASTRows);
            download(csvConfig)(csv);

            setProgressMessage({"status": "completed", "message": "AMR Predictions export completed."});
        } catch (error) {
            setProgressMessage(null);
                setUserErrorMessage(
                    'Failed to export AMR Predictions! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.'
                );
                throw new Error(`Request to export AMR Predictions for ${samples?.map(sample => sample.attributes.sample_name)} in dataset ${run.attributes.name} failed: ${error}`);
        } finally {
            setOneMenuItemClicked(false);
        }
    }, [setOneMenuItemClicked, isDatasetLevel, run, samples, kgASTRows, setProgressMessage, setUserErrorMessage]);

    return <MenuItem disabled={oneMenuItemClicked || dataLoading || !hasAnalyzedData} onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handleExportClick();
        handleMenuClose();
    }}>
        Export AMR Predictions
        {dataLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
    </MenuItem>

}